import React, { FC, useEffect } from "react";
import firebase from "firebase/app";
import { useHistory, useLocation } from "react-router-dom";
import { InputDialog } from "../../System";
import ROUTES from "../../utils/routes";
import { useForm } from "../../utils";
import { useNotifications } from "../../Notifications";

type Props = {
  isOpen: boolean;
};

const ForgotPasswordModal: FC<Props> = ({ isOpen }) => {
  const { values, setValue } = useForm({
    shouldUnregister: false,
    mode: "onBlur",
  });
  const history = useHistory();
  const location: { state: { email: string } } = useLocation();
  const { addNotification } = useNotifications();

  const emailExists = firebase.functions().httpsCallable("emailExists");
  const loginMigration = firebase
    .functions()
    .httpsCallable("confirmPasswordMigration");

  const resetUserPassword = async () => {
    try {
      if (!values.emailAddress?.trim()) {
        return;
      }

      const exists = await checkIfEmailExists(values.emailAddress);

      if (!exists) {
        throw "There is no user with that email address.";
      }

      await loginMigration({ email: values.emailAddress });
      await firebase.auth().sendPasswordResetEmail(values.emailAddress);
      addNotification({
        message:
          "Your password reset email will be sent if an associated email address is found on file. Please follow the directions provided there.",
        type: "success",
      });
    } catch (err) {
      addNotification({
        message: err?.message || err,
        type: "error",
      });
    }
  };

  const checkIfEmailExists = async (email: string) => {
    const signInMethods = await firebase
      .auth()
      .fetchSignInMethodsForEmail(email);
    const emailResult = await emailExists({ email });
    const { emailFound } = emailResult.data;

    if (signInMethods.length || emailFound) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (
      values.emailAddress === "" &&
      location &&
      location.state &&
      location.state.email
    ) {
      setValue("emailAddress", location.state.email);
    }
  }, []);

  return (
    <InputDialog
      value={values.emailAddress || ""}
      title="Forgot Password?"
      confirmButtonText="Reset Password"
      inputProps={{
        placeholder: "Your Social Curator Email Address",
        type: "email",
      }}
      isOpen={isOpen}
      onClose={() => history.push(ROUTES.user.login)}
      onChange={(value) => setValue("emailAddress", value)}
      onConfirm={() => resetUserPassword()}
    />
  );
};

export default ForgotPasswordModal;
